<template>
  <v-container class="py-12">
    <v-row>
      <v-col cols="12">
        <div class="title-container">
          <div class="title-block">
            <span class="page-title">Marcas e modelos</span>
            <v-breadcrumbs class="app-breadcrumb" divider="›" :items="[
              { text: 'Home', to: '/home' },
              { text: 'Veículos', to: '/veiculos', exact: true },
              { text: 'Marcas e modelos', disabled: true }
            ]" />
          </div>
          <div class="button-group">
            <v-btn outlined color="primary" elevation="0" @click="openDialog('brand')">
              <v-icon left small>mdi-plus</v-icon>Nova marca
            </v-btn>
            <v-btn color="primary" elevation="0" @click="openDialog('model')">
              <v-icon left small>mdi-plus</v-icon>Novo modelo
            </v-btn>
          </div>
        </div>
      </v-col>
    </v-row>

    <div class="catalog-grid">
      <v-card class="section-card">
        <v-card-title><div>Marcas<small>{{ brands.length }} cadastrada(s)</small></div></v-card-title>
        <v-text-field dense outlined hide-details clearable class="mx-4 mb-2" prepend-inner-icon="mdi-magnify"
          placeholder="Buscar marca" v-model="brandSearch" />
        <v-list dense class="brand-list">
          <v-list-item-group v-model="selectedBrandId" color="primary">
            <v-list-item :value="null">
              <v-list-item-content><v-list-item-title>Todas as marcas</v-list-item-title></v-list-item-content>
              <v-list-item-action-text>{{ models.length }}</v-list-item-action-text>
            </v-list-item>
            <v-list-item v-if="orphanCount" value="none">
              <v-list-item-content>
                <v-list-item-title class="orange--text text--darken-3">Sem marca</v-list-item-title>
              </v-list-item-content>
              <v-list-item-action-text>{{ orphanCount }}</v-list-item-action-text>
            </v-list-item>
            <v-list-item v-for="brand in filteredBrands" :key="brand.id" :value="brand.id">
              <v-list-item-content><v-list-item-title>{{ brand.name }}</v-list-item-title></v-list-item-content>
              <v-list-item-action-text class="mr-1">{{ brand.models_count }}</v-list-item-action-text>
              <v-list-item-action class="my-0">
                <v-menu offset-y left>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn icon small v-bind="attrs" v-on="on" @click.stop><v-icon small>mdi-dots-vertical</v-icon></v-btn>
                  </template>
                  <v-list dense>
                    <v-list-item @click="openDialog('brand', brand)"><v-list-item-title>Editar</v-list-item-title></v-list-item>
                    <v-list-item @click="openMerge(brand)"><v-list-item-title>Juntar com outra marca</v-list-item-title></v-list-item>
                    <v-list-item @click="removeBrand(brand)"><v-list-item-title class="red--text">Excluir</v-list-item-title></v-list-item>
                  </v-list>
                </v-menu>
              </v-list-item-action>
            </v-list-item>
          </v-list-item-group>
        </v-list>
      </v-card>

      <v-card class="section-card">
        <v-card-title>
          <div>Modelos<small>{{ selectedBrandLabel }}</small></div>
          <v-text-field dense outlined hide-details clearable class="model-search" prepend-inner-icon="mdi-magnify"
            placeholder="Buscar modelo" v-model="modelSearch" />
        </v-card-title>
        <v-data-table :headers="headers" :items="visibleModels" :search="modelSearch" :loading="loading"
          :items-per-page="25" :footer-props="{ itemsPerPageOptions: [25, 50, -1] }" class="app-table hover-table"
          no-data-text="Nenhum modelo cadastrado" no-results-text="Nenhum modelo encontrado" @click:row="openModel">
          <template v-slot:[`item.model`]="{ item }"><span class="font-weight-medium">{{ item.model }}</span></template>
          <template v-slot:[`item.brand`]="{ item }">
            <span v-if="item.brand_id">{{ item.brand }}</span>
            <v-chip v-else x-small color="orange" outlined>Sem marca</v-chip>
          </template>
          <template v-slot:[`item.rules_count`]="{ item }">
            <span :class="{ 'grey--text': !Number(item.rules_count) }">{{ Number(item.rules_count) || '-' }}</span>
          </template>
          <template v-slot:[`item.actions`]="{ item }">
            <div class="d-flex">
              <v-btn icon small @click.stop="openDialog('model', item)"><v-icon small>mdi-pencil-outline</v-icon></v-btn>
              <v-btn icon small :disabled="Number(item.vehicles_count) > 0" @click.stop="removeModel(item)">
                <v-icon small>mdi-delete-outline</v-icon>
              </v-btn>
            </div>
          </template>
        </v-data-table>
      </v-card>
    </div>

    <vehicle-catalog-dialog v-model="dialog.open" :kind="dialog.kind" :item="dialog.item" :brands="brands"
      :brand-id="typeof selectedBrandId === 'number' ? selectedBrandId : null" @saved="load" />

    <v-dialog v-model="merge.open" max-width="460">
      <v-card v-if="merge.brand">
        <v-card-title class="text-subtitle-1 font-weight-bold">Juntar marcas</v-card-title>
        <v-card-text>
          <p class="body-2">Os {{ merge.brand.models_count }} modelo(s) de <strong>{{ merge.brand.name }}</strong>
            passam para a marca escolhida e {{ merge.brand.name }} é excluída.</p>
          <v-autocomplete dense outlined hide-details label="Juntar em" :items="mergeTargets" item-text="name"
            item-value="id" v-model="merge.targetId" />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="merge.open = false">Cancelar</v-btn>
          <v-btn color="primary" depressed :disabled="!merge.targetId" :loading="merge.saving" @click="confirmMerge">Juntar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="detail.open" max-width="620">
      <v-card v-if="detail.model">
        <v-card-title class="text-subtitle-1 font-weight-bold">
          {{ detail.model.brand }} {{ detail.model.model }}
          <v-spacer />
          <v-btn icon small @click="detail.open = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-card-text>
          <div class="caption grey--text mb-2">
            {{ Number(detail.model.vehicles_count) }} veículo(s) com este modelo · regras de manutenção preventiva
          </div>
          <v-simple-table dense class="app-table">
            <thead>
              <tr><th>Item</th><th class="text-right">Intervalo do modelo</th><th class="text-right">Padrão do item</th><th></th></tr>
            </thead>
            <tbody>
              <tr v-if="!detail.model.rules.length">
                <td colspan="4" class="grey--text text-center py-4">
                  Sem regra própria: o modelo usa o intervalo padrão de cada item.
                </td>
              </tr>
              <tr v-for="rule in detail.model.rules" :key="rule.id" :class="{ 'grey--text': !rule.active }">
                <td>{{ rule.scheduling_name }}</td>
                <td class="text-right font-weight-medium">{{ interval(rule.interval_value, rule.interval_unit) }}</td>
                <td class="text-right">{{ interval(rule.default_interval_value, rule.interval_unit) }}</td>
                <td>
                  <v-btn x-small text color="primary" :to="`/manutencoes/programacoes/${rule.maintenance_scheduling_id}`">Abrir</v-btn>
                </td>
              </tr>
            </tbody>
          </v-simple-table>
          <div class="caption grey--text mt-3">As regras por modelo são definidas em Manutenções › Programações.</div>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import axios from 'axios'
import VehicleCatalogDialog from '@/layouts/components/vehicle/VehicleCatalogDialog.vue'

const UNITS = { km: 'km', mi: 'mi', days: 'dias', hours: 'horas' }

export default {
  name: 'VehicleCatalog',

  components: { VehicleCatalogDialog },

  data: () => ({
    loading: false,
    brands: [],
    models: [],
    brandSearch: '',
    modelSearch: '',
    selectedBrandId: null,
    dialog: { open: false, kind: 'brand', item: null },
    merge: { open: false, brand: null, targetId: null, saving: false },
    detail: { open: false, model: null },
    headers: [
      { text: 'Modelo', value: 'model' },
      { text: 'Marca', value: 'brand' },
      { text: 'Veículos', value: 'vehicles_count', align: 'end' },
      { text: 'Regras de manutenção', value: 'rules_count', align: 'end' },
      { text: '', value: 'actions', sortable: false, filterable: false }
    ]
  }),

  computed: {
    filteredBrands() {
      const search = (this.brandSearch || '').toLowerCase()
      return this.brands.filter((brand) => !search || brand.name.toLowerCase().includes(search))
    },

    orphanCount() {
      return this.models.filter((item) => !item.brand_id).length
    },

    visibleModels() {
      if (this.selectedBrandId === 'none') return this.models.filter((item) => !item.brand_id)
      if (this.selectedBrandId) return this.models.filter((item) => item.brand_id === this.selectedBrandId)
      return this.models
    },

    selectedBrandLabel() {
      if (this.selectedBrandId === 'none') return 'Modelos sem marca: edite para escolher a marca'
      const brand = this.brands.find((item) => item.id === this.selectedBrandId)
      return brand ? brand.name : 'Todas as marcas'
    },

    mergeTargets() {
      return this.merge.brand ? this.brands.filter((item) => item.id !== this.merge.brand.id) : []
    }
  },

  created() {
    this.load()
  },

  methods: {
    async load() {
      this.loading = true
      try {
        const [brands, models] = await Promise.all([
          axios.get('/api/v2/vehicle-brands'),
          axios.get('/api/v2/vehicle-models')
        ])
        this.brands = brands.data.content
        this.models = models.data.content
        if (typeof this.selectedBrandId === 'number' && !this.brands.some((item) => item.id === this.selectedBrandId)) {
          this.selectedBrandId = null
        }
      } catch (error) {
        this.notifyError(error)
      } finally {
        this.loading = false
      }
    },

    openDialog(kind, item = null) {
      this.dialog = { open: true, kind, item }
    },

    async openModel(item) {
      try {
        const response = await axios.get(`/api/v2/vehicle-models/${item.id}`)
        this.detail = { open: true, model: response.data.content }
      } catch (error) {
        this.notifyError(error)
      }
    },

    openMerge(brand) {
      this.merge = { open: true, brand, targetId: null, saving: false }
    },

    async confirmMerge() {
      this.merge.saving = true
      try {
        await axios.post(`/api/v2/vehicle-brands/${this.merge.brand.id}/merge`, { target_id: this.merge.targetId })
        this.$dialog.notify.success('Marcas unidas', { position: 'top-right', timeout: 2500 })
        this.selectedBrandId = this.merge.targetId
        this.merge.open = false
        await this.load()
      } catch (error) {
        this.notifyError(error)
      } finally {
        this.merge.saving = false
      }
    },

    async removeBrand(brand) {
      const confirmed = await this.$dialog.confirm({
        title: 'Excluir marca',
        text: `Excluir a marca ${brand.name}?`,
        actions: { false: 'Cancelar', true: 'Excluir' }
      })
      if (!confirmed) return
      try {
        await axios.delete(`/api/v2/vehicle-brands/${brand.id}`)
        await this.load()
      } catch (error) {
        this.notifyError(error)
      }
    },

    async removeModel(model) {
      const confirmed = await this.$dialog.confirm({
        title: 'Excluir modelo',
        text: `Excluir o modelo ${model.model}? As regras de manutenção dele também são removidas.`,
        actions: { false: 'Cancelar', true: 'Excluir' }
      })
      if (!confirmed) return
      try {
        await axios.delete(`/api/v2/vehicle-models/${model.id}`)
        await this.load()
      } catch (error) {
        this.notifyError(error)
      }
    },

    interval(value, unit) {
      if (!value) return '-'
      return `${Number(value).toLocaleString('pt-BR')} ${UNITS[unit] || unit || ''}`
    },

    notifyError(error) {
      this.$dialog.notify.error(this.$codes(error), { position: 'top-right', timeout: 5000 })
    }
  }
}
</script>

<style lang="scss" scoped>
.catalog-grid {
  display: grid;
  grid-template-columns: minmax(240px, 320px) 1fr;
  gap: 16px;
  align-items: start;

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
  }
}

.brand-list {
  max-height: 560px;
  overflow-y: auto;
}

.model-search {
  max-width: 260px;
}
</style>
